import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { ICampaignAnalytics } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ICreator } from '@pages/creators/creators.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { map, startWith } from 'rxjs';
import { IProfileMultiSelectState, ProfileMultiSelectFilterService } from './profile-multi-select-filter.service';

@Component({
  imports: [
    IconBadgeComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    NgxSkeletonLoaderModule,
    TranslateModule,
  ],
  selector: 'app-profile-general-filters',
  templateUrl: './profile-general-filters.component.html',
})
export class ProfileGeneralFiltersComponent {
  private readonly multiSelectFilterService = inject(ProfileMultiSelectFilterService);
  private readonly translate = inject(TranslateService);

  public readonly campaignAnalytics = input.required<ICampaignAnalytics | null>();
  public readonly initialSelectedCreatorIds = input<Set<string>>(new Set());
  public readonly loading = input.required<boolean>();

  public readonly creatorIdsChanged = output<Set<string>>();
  public readonly panelClosed = output<void>();
  public readonly selectionState = signal<IProfileMultiSelectState<string>>(
    this.multiSelectFilterService.createUnfilteredSelectionState<string>(),
  );
  private panelSelectionSnapshot: string | null = null;
  public readonly languageChange = toSignal(
    this.translate.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translate.currentLang),
    ),
    { initialValue: this.translate.currentLang },
  );

  public readonly creatorOptions = computed((): ICreator[] => {
    return Array.from(this.campaignAnalytics()?.creators?.values() ?? []).map((creator) => ({
      icon: creator.icon,
      id: creator.id,
      name: creator.name,
    }));
  });

  public readonly totalCreatorCount = computed(() => this.creatorOptions().length);

  public readonly selectedCreatorIds = computed(() => new Set(this.selectionState().uiSelectedValues));

  public readonly emptyLabel = computed(() => {
    this.languageChange();

    const totalCreatorCount = this.totalCreatorCount();

    if (totalCreatorCount === 0) {
      return this.translate.instant('pages.campaignDetails.filter.allCreators');
    }

    return `${this.translate.instant('pages.campaignDetails.filter.allCreators')} (${totalCreatorCount})`;
  });

  public readonly triggerLabel = computed(() => {
    this.languageChange();

    const orderedSelectedCreators = this.creatorOptions().filter((creator) =>
      this.selectedCreatorIds().has(creator.id),
    );

    return this.multiSelectFilterService.buildSelectionSummary(
      orderedSelectedCreators[0]?.name ?? '',
      orderedSelectedCreators.length,
      (this.translate.instant('shared.more') as string).toLowerCase(),
    );
  });

  constructor() {
    effect(() => {
      const availableCreatorIds = this.creatorOptions().map((creator) => creator.id);
      const nextState: IProfileMultiSelectState<string> = this.multiSelectFilterService.syncSelectionState(
        availableCreatorIds,
        this.initialSelectedCreatorIds(),
        this.selectionState(),
      );

      if (!this.selectionStatesAreEqual(this.selectionState(), nextState)) {
        this.selectionState.set(nextState);
      }
    });
  }

  public onSelectionChange(event: MatSelectChange): void {
    const nextSelectedCreatorIds: string[] = event.value ?? [];
    const nextState: IProfileMultiSelectState<string> =
      nextSelectedCreatorIds.length === 0
        ? this.multiSelectFilterService.createUnfilteredSelectionState<string>()
        : this.multiSelectFilterService.createExplicitSelectionState(nextSelectedCreatorIds);

    this.selectionState.set(nextState);
    this.emitCreatorIds();
  }

  public onSelectAll(event: Event): void {
    event.stopPropagation();

    this.selectionState.set(
      this.multiSelectFilterService.createExplicitSelectionState(this.creatorOptions().map((creator) => creator.id)),
    );
    this.emitCreatorIds();
  }

  public onUnselectAll(event: Event): void {
    event.stopPropagation();

    this.selectionState.set(this.multiSelectFilterService.createUnfilteredSelectionState<string>());
    this.emitCreatorIds();
  }

  public onOpenedChange(opened: boolean): void {
    if (opened) {
      this.panelSelectionSnapshot = this.getSelectionSnapshot();
    }
  }

  public onPanelClosed(): void {
    const nextSelectionSnapshot = this.getSelectionSnapshot();
    const selectionChanged =
      this.panelSelectionSnapshot !== null && this.panelSelectionSnapshot !== nextSelectionSnapshot;

    this.panelSelectionSnapshot = null;

    if (!selectionChanged) {
      return;
    }

    this.panelClosed.emit();
  }

  private emitCreatorIds(): void {
    this.creatorIdsChanged.emit(
      this.multiSelectFilterService.normalizeValuesForEmission(
        this.selectionState(),
        this.creatorOptions().map((creator) => creator.id),
      ),
    );
  }

  private getSelectionSnapshot(): string {
    return JSON.stringify(
      Array.from(
        this.multiSelectFilterService.normalizeValuesForEmission(
          this.selectionState(),
          this.creatorOptions().map((creator) => creator.id),
        ),
      ).sort(),
    );
  }

  private selectionStatesAreEqual(
    left: IProfileMultiSelectState<string>,
    right: IProfileMultiSelectState<string>,
  ): boolean {
    return (
      left.presentation === right.presentation &&
      left.uiSelectedValues.length === right.uiSelectedValues.length &&
      left.uiSelectedValues.every((value, index) => value === right.uiSelectedValues[index])
    );
  }
}
